import React from 'react';

/**
 * How a value that changed is shown: the old one struck through, the new one in bold.
 * The owner of BUSINESS_RULES#value-change-display.
 *
 * Built for the vendor-invoice change card (docs/DESIGN.md), and to replace the two places that
 * already show a price moving, each in its own way: the quotation line's "Cost moved A → B" box and
 * the Master Catalogue's previous-cost cell.
 *
 * It formats nothing. Money arrives as the caller's display string, in the currency it was invoiced
 * in, so this component cannot restate a figure. `<del>` and `<ins>` carry the meaning in the HTML
 * itself; the hidden "was" and "now" are for screen readers, which announce neither tag.
 */
export interface ValueChangeProps {
  /** The value before. Leave it out for a value that did not exist before. */
  from?: React.ReactNode;
  to: React.ReactNode;
  /** Trailing context, e.g. "+64%". */
  note?: React.ReactNode;
}

export const ValueChange: React.FC<ValueChangeProps> = ({ from, to, note }) => {
  const hadValue = from != null && from !== '';
  return (
    <span className="tabular-nums">
      {hadValue ? (
        <del className="mr-1.5 text-slate-500 decoration-slate-400">
          <span className="sr-only">was </span>
          {from}
        </del>
      ) : null}
      <ins className="font-semibold text-slate-900 no-underline">
        {hadValue ? <span className="sr-only">now </span> : null}
        {to}
      </ins>
      {note ? <span className="ml-1.5 text-xs text-slate-500">{note}</span> : null}
    </span>
  );
};

export default ValueChange;
